import React from "react";
import { Button, FormControl, FormLabel, Select, SimpleGrid, Textarea, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import reviewApi from "../api/reviewApi";
import ModulePageShell from "../components/common/ModulePageShell";
import OperationalPanel from "../components/common/OperationalPanel";
import useGoalCycleOptions from "../hooks/useGoalCycleOptions";
import usePerformanceUsers from "../hooks/usePerformanceUsers";
import useReviewTemplateOptions from "../hooks/useReviewTemplateOptions";
import performanceQueryKeys from "../utils/queryKeys";

const ReviewGenerationPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { goalCycleOptions, isLoading: goalCyclesLoading } = useGoalCycleOptions();
  const { userOptions, isLoading: usersLoading } = usePerformanceUsers();
  const { reviewTemplateOptions, isLoading: templatesLoading } = useReviewTemplateOptions();

  const { register, handleSubmit } = useForm({
    defaultValues: {
      goalCycleId: "",
      performanceTemplateId: "",
      employeeId: "",
      notes: "",
    },
  });

  const generateMutation = useMutation({
    mutationFn: reviewApi.generatePerformanceReviews,
    onSuccess: () => {
      toast({ title: "Review generation started", status: "success", duration: 2500 });
      queryClient.invalidateQueries({ queryKey: performanceQueryKeys.reviews.all });
      navigate("/performance/reviews/team");
    },
    onError: (error) => {
      toast({
        title: "Unable to generate reviews",
        description: error?.response?.data?.message || error?.message || "Please check the selected template and employee.",
        status: "error",
        duration: 4000,
      });
    },
  });

  const onGenerate = (values) => {
    generateMutation.mutate({
      goalCycleId: values.goalCycleId,
      performanceTemplateId: values.performanceTemplateId,
      employeeSelection: {
        mode: "EMPLOYEES",
        employeeIds: [values.employeeId],
      },
    });
  };

  return (
    <ModulePageShell
      title="Generate reviews"
      description="Create review records from a PMS template and goal cycle."
      actions={
        <Button size="sm" colorScheme="blue" onClick={handleSubmit(onGenerate)} isLoading={generateMutation.isPending}>
          Generate reviews
        </Button>
      }
    >
      <OperationalPanel>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl isRequired>
            <FormLabel>Goal cycle ID</FormLabel>
            <Select size="sm" placeholder={goalCyclesLoading ? "Loading goal cycles" : "Select goal cycle"} isDisabled={goalCyclesLoading} {...register("goalCycleId", { required: true })}>
              {goalCycleOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Review Template</FormLabel>
            <Select size="sm" placeholder={templatesLoading ? "Loading templates" : "Select PMS template"} isDisabled={templatesLoading} {...register("performanceTemplateId", { required: true })}>
              {reviewTemplateOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Employee</FormLabel>
            <Select size="sm" placeholder={usersLoading ? "Loading employees" : "Select employee"} isDisabled={usersLoading} {...register("employeeId", { required: true })}>
              {userOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl gridColumn={{ md: "1 / -1" }}>
            <FormLabel>Notes</FormLabel>
            <Textarea size="sm" {...register("notes")} />
          </FormControl>
        </SimpleGrid>
      </OperationalPanel>
    </ModulePageShell>
  );
};

export default ReviewGenerationPage;
